import {
  Container,
  FullDialog,
  Typography,
  Grid,
  Button,
  ActionLayout,
  SelectController,
  InputLabel,
} from '@alice-financial/pretext-ui'
import * as React from 'react'
import { EmployeeRow, OptionalStatusOverrideEnum } from './types'
import {
  EmployeePayrollSystemStatusEnum,
  statusHumanize,
  EmployeeAccountStatus,
} from '@alice-financial/alice-sdk'
import { FormattedMessage } from 'react-intl'
import { useForm } from 'react-hook-form'

export type StatusChooserDialogProps = {
  open: boolean
  onClose: () => void
  onSelect: (status: OptionalStatusOverrideEnum) => void
  employee: EmployeeRow
}

const StatusExplainer = ({
  selectedStatus,
  currentOverrideStatus,
}: {
  selectedStatus: OptionalStatusOverrideEnum
  currentOverrideStatus: OptionalStatusOverrideEnum
}) => {
  return (
    <Typography variant="body2" gutterBottom sx={{ marginTop: 2 }}>
      {statusNote(selectedStatus, currentOverrideStatus)}
    </Typography>
  )
}

// help explain to the admin what the implications of their selected status override will be, basedon the diff between
// status (proposed) and currentOverrideStatus
const statusNote = (
  status: OptionalStatusOverrideEnum,
  currentOverrideStatus: OptionalStatusOverrideEnum
) => {
  // don't show any note if current status matches override, that would just be UI noise
  if (status === currentOverrideStatus) return ''
  if (status === '' && currentOverrideStatus !== null) {
    // user has selected the blank value from dropdown to remove the override
    return 'This will remove the current override, and Alice will use whatever value we get from your payroll system during our next sync.'
  }
  switch (status) {
    case EmployeePayrollSystemStatusEnum.Active:
      return 'Setting an employee to Active will allow them to enroll or remain enrolled if they already are.'
    case EmployeePayrollSystemStatusEnum.Inactive:
      return 'Setting an employee to Inactive will immediately end their benefits.'
    case EmployeePayrollSystemStatusEnum.PaidLeave:
      return 'Setting an employee to Paid Leave will allow them to enroll or remain enrolled if they already are. If you want to suspend their Alice benefits in addition to marking them on paid leave, please email us at admin-support@thisisalice.com'
    case EmployeePayrollSystemStatusEnum.UnpaidLeave:
      return 'Setting an employee to Unpaid Leave will immediately end their benefits.'
    case EmployeePayrollSystemStatusEnum.Ineligible:
      return 'Setting an employee to Ineligible will immediately end their benefits.'
    case EmployeePayrollSystemStatusEnum.Terminated:
      return 'Setting an employee to Terminated will immediately end their benefits.'
    default:
      return ''
  }
}

export const StatusChooserDialog = ({ open, onClose, onSelect, employee }: StatusChooserDialogProps) => {
  const defaultStatus = employee.rosterOverrideStatus
  const { control, handleSubmit, watch } = useForm<{ status: OptionalStatusOverrideEnum }>({
    defaultValues: { status: defaultStatus },
  })
  const selectedStatus = watch('status')
  const onSubmit = handleSubmit(({ status }) => onSelect(status))

  const statuses = Object.values(EmployeePayrollSystemStatusEnum)
  // processing a termination requires additional info which we do in FinalPaycheckDialog, so exclude that from this dropdown
  const dropdownStatuses = statuses.filter((s) => s !== EmployeePayrollSystemStatusEnum.Terminated)

  if (!open) return null

  return (
    <FullDialog
      open={open}
      onClose={onClose}
      title={
        <Typography variant="h2">
          Update status for {employee.firstName} {employee.lastName}
        </Typography>
      }
    >
      <Container>
        <Typography variant="body2" gutterBottom>
          {`${employee.firstName}'s Alice account status is based on their status in your payroll system.`}
        </Typography>
        <Typography variant="body2" gutterBottom>
          Alice syncs regularly with your payroll system, so as long as that data is accurate, Alice will
          reflect it correctly. If you need to override that data, you can do so here, and we will
          automatically update their Alice enrollment to reflect this status.
        </Typography>

        <ul>
          <Typography variant="body2" component="li">
            <strong>Alice account status:</strong> {`${statusHumanize(employee.accountStatus ?? '')}`}
          </Typography>
          <Typography variant="body2" component="li">
            <strong>Payroll status:</strong>{' '}
            {employee.payrollSystemStatus ? statusHumanize(employee.payrollSystemStatus) : <em>(blank)</em>}
          </Typography>
          {employee.rosterOverrideStatus && (
            <Typography variant="body2" component="li">
              <strong>Payroll status override:</strong> {statusHumanize(employee.rosterOverrideStatus)}
            </Typography>
          )}
        </ul>

        {employee.accountStatus !== EmployeeAccountStatus.Terminated && (
          <Typography variant="body2" gutterBottom>
            To mark an employee as Terminated,{' '}
            <Button
              size="small"
              variant="outlined"
              onClick={() => onSelect(EmployeePayrollSystemStatusEnum.Terminated)}
            >
              click here
            </Button>
            .
          </Typography>
        )}

        <Typography variant="body2" gutterBottom>
          To update their status to a value other than Terminated, choose it below.
          {employee.rosterOverrideStatus &&
            ' To remove their current override, choose the blank value and click "Confirm".'}
        </Typography>
        <InputLabel id="override-label">Override status</InputLabel>
        <SelectController name="status" control={control} labelId="override-label">
          <option value="" />
          {dropdownStatuses.map((status) => (
            <option key={status} value={status}>
              {statusHumanize(status)}
            </option>
          ))}
        </SelectController>

        <StatusExplainer
          selectedStatus={selectedStatus}
          currentOverrideStatus={employee.rosterOverrideStatus}
        />

        <ActionLayout
          primary={
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Button variant="outlined" onClick={onClose}>
                  <FormattedMessage id="common.cancel.cancel" />
                </Button>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Button variant="contained" onClick={onSubmit}>
                  Confirm
                </Button>
              </Grid>
            </Grid>
          }
        />
      </Container>
    </FullDialog>
  )
}
